import { useState } from "react";
import { Loader2, Mail, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSeo } from "@/hooks/useSeo";
import { useT } from "@/i18n";

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [limited, setLimited] = useState("");

  useSeo({
    title: t("contact.seo.title"),
    description: t("contact.seo.desc"),
    canonical: `${window.location.origin}${window.location.pathname}`,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact.toast.sent"));
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 429) setLimited(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-5xl px-4 py-14 sm:px-8">
      <div className="grid gap-12 md:grid-cols-[1fr_1.1fr]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{t("contact.eyebrow")}</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("contact.title")}</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{t("contact.intro")}</p>

          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold text-slate-900">{t("contact.emailLabel")}</p>
                <a href="mailto:info@direktoribiz.my" data-testid="contact-email-link" className="text-slate-600 hover:text-primary">
                  info@direktoribiz.my
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MessageSquare className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold text-slate-900">YBD INFINITY GLOBAL</p>
                <p className="text-slate-600">{t("footer.regno")}: 202603212324 (JM1048394-W)</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
          {sent ? (
            <div data-testid="contact-success" className="py-10 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50">
                <Send className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="mt-5 text-base font-bold text-slate-900 md:text-lg">{t("contact.sent.title")}</h2>
              <p className="mt-2 text-sm text-slate-600">
                {t("contact.sent.body", { name: form.name || t("contact.you") })}
              </p>
              <Button
                variant="outline"
                className="mt-6"
                data-testid="contact-send-another-btn"
                onClick={() => {
                  setForm({ name: "", email: "", phone: "", subject: "", message: "" });
                  setSent(false);
                }}
              >
                {t("contact.sent.again")}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              {limited && (
                <p data-testid="contact-rate-limit" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  {limited}
                </p>
              )}
              <div>
                <Label htmlFor="c-name">{t("contact.field.name")}</Label>
                <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="mt-1.5" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="c-email">{t("contact.field.email")}</Label>
                  <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-phone">{t("contact.field.phone")}</Label>
                  <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-subject">{t("contact.field.subject")}</Label>
                <Input id="c-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="c-message">{t("contact.field.message")}</Label>
                <Textarea id="c-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} className="mt-1.5" />
              </div>
              <Button type="submit" disabled={busy} data-testid="contact-submit-btn" className="w-full">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                {t("contact.submit")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
